import { Record as ImmutableRecord, List, Set } from "immutable";

import { FeatureToggleState } from "@config/featureToggles";

import {
  AGENT_PREFERENCE,
  BookingTasks,
  CLEANING_FREQUENCY,
  IAgent,
  PricePlan,
  KEY_PICK,
  CleaningProductsType,
  NonStandardFrequencyShifts,
  PetsDetails,
} from "@typings/booking";
import {
  AVAILABLE_CURRENCIES,
  PROMO_CODE_NOTIFICATION,
  PAYMENT_METHODS,
} from "@typings/globals";
import { LOADING_STATE } from "@typings/reduxThunkActions";
import { PriceSubpartDTO, PriceSubpart } from "@typings/priceSubparts";
import { AvailabilitiesCalendarV3 } from "@typings/bookingCalendarV3";

export const LOCAL_STORAGE_KEY = "store:booking";

export interface BookingReducer {
  tasks: ImmutableRecord<BookingTasks>;
  calendar: ImmutableRecord<AvailabilitiesCalendarV3>;
  execution: {
    date: string | null;
    time: string | null;
  };
  pricing: ImmutableRecord<BookingPricing>;
  pricingLoadingState: LOADING_STATE;
  priceWillChange: boolean;
  frequencies: List<CLEANING_FREQUENCY>;
  frequency: CLEANING_FREQUENCY;
  frequenciesLoadingState: LOADING_STATE;
  pricePlans: List<ImmutableRecord<PricePlan>>;
  pricePlansLoadingState: LOADING_STATE;
  serviceVoucherPlans: List<ImmutableRecord<PricePlan>>;
  serviceVouchersLoadingState: LOADING_STATE;
  pricePlanUuid: string | null;
  preselectedAgentUuid: string | null;
  isChoosingAnyBatmaid: boolean;
  hasPets: boolean;
  petsDetails: ImmutableRecord<PetsDetails>;
  agentPreference: AGENT_PREFERENCE;
  serviceLocationId: number;
  reservationUuid: string;
  isCleaningChecklistAvailable: boolean;
  previousAgents: List<ImmutableRecord<IAgent>>;
  previousAgentsLoadingState: LOADING_STATE;
  voucherToken: string;
  voucherMessage: string;
  voucherStatus: PROMO_CODE_NOTIFICATION;
  specialInstructions: string | null;
  keyPick: KEY_PICK;
  locationComment: string;
  locationUuid: string;
  form: ImmutableRecord<BookingRegistrationForm>;
  mainLocationUuid: string;
  availablePaymentMethods: List<PAYMENT_METHODS>;
  selectedPaymentMethod: PAYMENT_METHODS | null;
  paymentMethodsLoadingState: LOADING_STATE;
  templateJobId: string | null;
  queryParams: string;
  cleaningProducts: CleaningProductsType;
  selectedCleaningProductsIds: Set<string>;
  nonStandardFrequencyShifts: NonStandardFrequencyShifts[];
  contactAfterBooking: boolean;
  hasServiceVouchers: boolean;
  hasUrssaf: boolean;
  selectedTimeSlotId: string | null;
}

export interface BookingRegistrationForm {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address: string;
  serviceLocationId: number;
  floorAndDoor: string;
  entryCode: string;
  serviceVoucher?: string;
  hasPets?: boolean;
  marketingConsent?: boolean;
}

export interface PriceItem {
  amount: number;
  frequency: CLEANING_FREQUENCY;
}

export interface BookingPricingRaw {
  oncePricePerHour: number;
  weeklyPricePerHour: number;
  every2WeeksPricePerHour: number;
  every4WeeksPricePerHour: number;
  moreOftenPricePerHour: number;
  displayedPriceForFirstBooking: number;
  displayedHoursForFirstBooking: number;
  currency: AVAILABLE_CURRENCIES;
  hasSpecialPricing: boolean;
  specialPricingTranslatedMessage: string | null;
  totalCheckoutPrice?: number;
  totalServiceVouchers: number | null;
  firstCleaningPrice: number;
  discountsToDisplay: PriceSubpartDTO[];
  basePricePerHour: number;
}

export interface BookingPricing {
  pricesPerFrequency: List<PriceItem>;
  displayedPriceForFirstBooking: number;
  displayedHoursForFirstBooking: number;
  currency: AVAILABLE_CURRENCIES;
  hasSpecialPricing: boolean;
  specialPricingTranslatedMessage: string | null;
  totalCheckoutPrice: number | null;
  totalServiceVouchers: number | null;
  firstCleaningPrice: number;
  discountsToDisplay: List<PriceSubpart>;
  basePricePerHour: number;
}

export interface TelemetryCalendarContext {
  duration: number;
  frequency: CLEANING_FREQUENCY;
  displayedSlots: string[];
}

export interface BookingRegistrationRequest {
  address: string;
  floorAndDoor: string;
  entryCode: string;
  hasPets: boolean;
}
export interface CheckoutParams {
  targetRoute?: string;
  originRoute?: string;
  featureToggles?: Partial<FeatureToggleState>;
}
